import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppStateService } from '@services/app-state.service';
import { GastroService } from '@services/gastro.service';
import { ToastService } from '@services/toast.service';
import { Router } from '@angular/router';
import type { MasterCard } from '@models/reservation.interface';
import { ReservationService } from '@app/services';
import { LoadingComponent } from '@components/shared/loading.component';
import { GuestPackageType } from '@app/utils';

@Component({
  selector: 'app-summary-send',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    LoadingComponent
  ],
  templateUrl: './summary-send.component.html',
  styleUrls: ['./summary-send.component.scss']
})
export class SummarySendComponent implements OnInit {
  loading: boolean = false;
  submitted: boolean = false;
  error: string = '';
  successMessage: string = '';
  deletingCard: { [key: string]: boolean } = {};
  isAddingCard: { [key: string]: boolean } = {};
  isCreatingCard: { [key: string]: boolean } = {};

  // Sunbed number editing
  editingSunbedKey: string | null = null;  // Format: "masterCardId-sunbedId"
  editedSunbedNumber: string = '';
  isUpdatingSunbed: { [key: string]: boolean } = {};

  constructor(
    private appState: AppStateService,
    private gastroService: GastroService,
    private toastService: ToastService,
    private location: Location,
    private router: Router,
    private reservationService: ReservationService,
) {}

  ngOnInit(): void {
    this.submitted = this.appState.isSubmitted;
  }

  get reservation() {
    return this.appState.reservation;
  }

  get isValamarGuest() {
    return this.appState.isValamarGuest;
  }

  get dailyCheckinCompleted() {
    return this.appState.dailyCheckinCompleted;
  }

  get valamarBooking() {
    return this.appState.valamarBooking;
  }

  get vLevel() {
    return this.appState.guestPackage == GuestPackageType.VLEVEL;
  }

  get dailyBudget() {
    return this.appState.dailyBudget;
  }

  // Get master cards for selected date from reservation.gastro.masterCards
  get todayMasterCards(): MasterCard[] {
    if (!this.reservation?.gastro?.masterCards) return [];
    const selectedDate = this.appState.selectedDate;
    if(selectedDate) {
      return this.reservation.gastro.masterCards.filter(mc =>
        mc.transferDate?.startsWith(selectedDate)
      );
    }
    return this.reservation.gastro.masterCards;
  }

  get totalBudget() {
    return this.todayMasterCards.reduce((sum, mc) => sum + (mc.initialBalance || 0), 0);
  }

  get totalOverdraft() {
    return this.todayMasterCards.reduce((sum, mc) => sum + (mc.allowedOverdraft || 0), 0);
  }

  get totalCredit() {
    return this.todayMasterCards.reduce((sum, mc) =>
      sum + (mc.initialBalance || 0) + (mc.allowedOverdraft || 0), 0);
  }

  get totalWristbands() {
    // Count only additional cards (real wristbands), not master cards
    return this.todayMasterCards.reduce((sum, mc) =>
      sum + (mc.additionalCards?.length || 0), 0);
  }

  get totalChairs() {
    return this.todayMasterCards.reduce((sum, mc) =>
      sum + (mc.sunbeds?.length || 0), 0);
  }

  get displayDate(): string {
    if (this.todayMasterCards.length === 0) return '';

    const transferDate = this.todayMasterCards[0].transferDate;
    if (!transferDate) return '';

    // Format: "2026-05-29" -> "29.05.2026"
    const parts = transferDate.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}.${month}.${year}`;
    }

    // If format is "29.5.2026", return as is
    return transferDate.split(' ')[0];
  }

  get currency() {
    return this.reservation?.currency || 'EUR';
  }

  onSubmit(): void {
    console.log("ZAVRŠI->",this.reservation);
    if (!this.reservation) {
      this.error = 'Nema podataka o rezervaciji.';
      return;
    }

    this.error = '';
    this.loading = true;

    if(this.isTodayReservation()){
      this.reservationService.updateReservation(this.reservation).subscribe({
        next: (res) => {
          console.log("Update reservation res-<",res);
          this.loading = false;
          this.submitted = true;
          this.appState.setIsSubmitted(true);
        },
        error: (err) => {
          console.error("Update reservation error-<",err);
          this.loading = false;
          this.error = err.message || 'Greška prilikom ažuriranja rezervacije.';
          this.toastService.error(this.error);
        }
      });
    } else {
      // If not today reservation, just mark as submitted
      this.loading = false;
      this.submitted = true;
      this.appState.setIsSubmitted(true);
    }
  }

  onBack(): void {
    this.appState.setCurrentStep(3);
    this.location.back();
  }

  onNewCheckIn(): void {
    this.appState.resetToStep1();
    this.submitted = false;
    this.error = '';
    this.successMessage = '';
    this.router.navigate(['/search']);
  }

  /**
   * Delete additional card (wristband) from Gastro
   */
  deleteAdditionalCard(masterCardId: string, additionalCardId: string): void {
    const cardKey = `${masterCardId}-${additionalCardId}`;
    this.deletingCard[cardKey] = true;
    this.error = '';

    this.gastroService.deleteAdditionalCard(masterCardId, additionalCardId).subscribe({
      next: (response) => {
        this.deletingCard[cardKey] = false;

        if (response.success) {
          // Show success toast
          this.toastService.success(`Narukvica ${additionalCardId} je uspješno obrisana`);
          // Remove from local gastro data
          this.removeAdditionalCardFromGastro(masterCardId, additionalCardId);
          // Trigger BehaviorSubject update
          this.appState.setReservation(this.reservation);
        } else {
          this.error = response.message || 'Failed to delete additional card';
          this.toastService.error(this.error);
        }
      },
      error: (err) => {
        this.deletingCard[cardKey] = false;
        this.error = err.message || 'Failed to delete additional card';
        this.toastService.error(this.error);
      }
    });
  }

  /**
   * Delete master card (group) from Gastro
   */
  deleteMasterCard(masterCardId: string): void {
    this.deletingCard[masterCardId] = true;
    this.error = '';

    this.gastroService.deleteMasterCard(masterCardId).subscribe({
      next: (response) => {
        this.deletingCard[masterCardId] = false;

        if (response.success) {
          // Show success toast
          this.toastService.success(`Grupa ${masterCardId} je uspješno obrisana`);
          // Remove from local gastro data
          this.removeMasterCardFromGastro(masterCardId);
          // Trigger BehaviorSubject update
          this.appState.setReservation(this.reservation);
        } else {
          this.error = response.message || 'Failed to delete master card';
          this.toastService.error(this.error);
        }
      },
      error: (err) => {
        this.deletingCard[masterCardId] = false;
        this.error = err.message || 'Failed to delete master card';
        this.toastService.error(this.error);
      }
    });
  }

  /**
   * Remove additional card from local gastro data
   */
  private removeAdditionalCardFromGastro(masterCardId: string, additionalCardId: string): void {
    const reservation = this.appState.reservation;
    if (!reservation?.gastro?.masterCards) return;

    const masterCard = reservation.gastro.masterCards.find(mc => mc.id === masterCardId);
    if (masterCard?.additionalCards) {
      masterCard.additionalCards = masterCard.additionalCards.filter(card => card.id !== additionalCardId);
    }
  }

  /**
   * Remove master card from local gastro data
   */
  private removeMasterCardFromGastro(masterCardId: string): void {
    const reservation = this.appState.reservation;
    if (!reservation?.gastro?.masterCards) return;

    reservation.gastro.masterCards = reservation.gastro.masterCards.filter(mc => mc.id !== masterCardId);
  }

  isDeleting(masterCardId: string, additionalCardId?: string): boolean {
    const key = additionalCardId ? `${masterCardId}-${additionalCardId}` : masterCardId;
    return this.deletingCard[key] || false;
  }

  showAddCardInput(masterCardId: string): void {
    this.isAddingCard[masterCardId] = true;
  }

  cancelAddingCard(masterCardId: string): void {
    this.isAddingCard[masterCardId] = false;
  }

  createAdditionalCard(masterCardId: string, cardId: string): void {
    if (!cardId || cardId.trim() === '') {
      this.toastService.error('Unesite ID narukvice');
      return;
    }

    this.isCreatingCard[masterCardId] = true;
    this.error = '';

    this.gastroService.createAdditionalCard(masterCardId, cardId.trim()).subscribe({
      next: (response) => {
        this.isCreatingCard[masterCardId] = false;

        if (response.success) {
          // Show success toast
          this.toastService.success(`Narukvica ${cardId} je uspješno dodana`);
          // Add to local gastro data
          this.addAdditionalCardToGastro(masterCardId, cardId.trim());
          // Hide input
          this.isAddingCard[masterCardId] = false;
          // Trigger BehaviorSubject update
          this.appState.setReservation(this.reservation);
          console.log("Added additional card to reservation ",this.reservation?.gastro.masterCards);
        } else {
          this.error = response.message || 'Failed to create additional card';
          this.toastService.error(this.error);
        }
      },
      error: (err) => {
        this.isCreatingCard[masterCardId] = false;
        this.error = err.message || 'Failed to create additional card';
        this.toastService.error(this.error);
      }
    });
  }

  private addAdditionalCardToGastro(masterCardId: string, cardId: string): void {
    const reservation = this.appState.reservation;
    if (!reservation?.gastro?.masterCards) return;

    const masterCard = reservation.gastro.masterCards.find(mc => mc.id === masterCardId);
    if (masterCard) {
      if (!masterCard.additionalCards) {
        masterCard.additionalCards = [];
      }
      masterCard.additionalCards.push({ id: cardId });
    }
  }

  // Check if selected date is today (to disable editing for past dates)
  isTodayReservation(date?:string): boolean {
    let selectedDate = this.appState.selectedDate;
    if (!selectedDate) {
      if(date != null) {
        selectedDate = date;
      }else{
        return false;
      }
    }

    const today = new Date().toISOString().split('T')[0];
    return selectedDate === today;
  }

  parseDate(date?: string) {
    return this.appState.parseDate(date);
  }

    getGuestName(): string {
      return this.reservation?.fullName || `${this.reservation?.name || ''} ${this.reservation?.lastName || ''}`.trim() || 'Nepoznat gost';
  }

  // ============================================================================
  // SUNBED NUMBER EDITING
  // ============================================================================

  /**
   * Start editing a sunbed number
   */
  startEditingSunbed(masterCardId: string, sunbedId: string): void {
    const key = `${masterCardId}-${sunbedId}`;
    this.editingSunbedKey = key;

    // Find the sunbed and get its current number
    const masterCard = this.todayMasterCards.find(mc => mc.id === masterCardId);
    const sunbed = masterCard?.sunbeds?.find(s => s.id === sunbedId);
    this.editedSunbedNumber = sunbed?.number || '';
  }

  /**
   * Save the edited sunbed number
   */
  saveSunbedNumber(masterCardId: string, sunbedId: string): void {
    const key = `${masterCardId}-${sunbedId}`;
    const reservation = this.appState.reservation;
    if (!reservation?.gastro?.masterCards) return;

    const masterCard = reservation.gastro.masterCards.find(mc => mc.id === masterCardId);
    if (!masterCard?.sunbeds) return;

    const sunbed = masterCard.sunbeds.find(s => s.id === sunbedId);
    if (!sunbed) return;

    // Update the sunbed number locally
    sunbed.number = this.editedSunbedNumber.trim();

    // Exit edit mode
    this.editingSunbedKey = null;
    this.editedSunbedNumber = '';

    // Trigger BehaviorSubject update to refresh UI
    this.appState.setReservation(reservation);
  }

  /**
   * Cancel editing sunbed number
   */
  cancelSunbedEdit(): void {
    this.editingSunbedKey = null;
    this.editedSunbedNumber = '';
  }

  /**
   * Handle Enter key press in edit mode
   */
  onSunbedEditKeyDown(event: KeyboardEvent, masterCardId: string, sunbedId: string): void {
    const key = `${masterCardId}-${sunbedId}`;
    if (event.key === 'Enter') {
      this.saveSunbedNumber(masterCardId, sunbedId);
    } else if (event.key === 'Escape') {
      this.cancelSunbedEdit();
    }
  }

  /**
   * Check if a specific sunbed is being edited
   */
  isEditingSunbed(masterCardId: string, sunbedId: string): boolean {
    const key = `${masterCardId}-${sunbedId}`;
    return this.editingSunbedKey === key;
  }
}
